<script setup lang="ts">
import DeleteDocumentDialog from "@/components/dialogs/DeleteDocumentDialog.vue";
import ButtonLink from "@/components/partials/ButtonLink.vue";

import useDocusign from "@/plugins/docusign";
import { Directory } from "@/types/documents/Directory";
import { Document } from "@/types/editor/Document";
import axios from "axios";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";

import useGTM from "@/utils/gtm";
import translateDocumentName from "@/utils/translateDocumentName";

const emits = defineEmits(["refreshPage", "openDocument", "createDirectory", "uploadDocument"]);

const { t, locale } = useI18n();
const { uploadDocumentToDocusign } = useDocusign();
const router = useRouter();

interface DocumentStatus {
  name: string;
  color: string;
  value: string;
}

const {
  document,
  documentStatuses,
  directories,
  showSignButton = true,
  showDeleteButton,
} = defineProps<{
  document: Document;
  documentStatuses: DocumentStatus[];
  directories: Directory[];
  showSignButton?: boolean;
  showDeleteButton?: boolean;
}>();

const documentStatusColor = computed(() => {
  const status = documentStatuses.find((status: DocumentStatus) => status.name === document.status);
  return status ? status.color : "";
});

const filteredDirectoryList = computed(() => {
  if (document.directory == null) {
    return directories.filter((directory) => directory.id !== null);
  }

  return directories.filter(
    (directory) =>
      directory.id !== directories.find((dir) => dir.name === document.directory?.name)?.id,
  );
});

const documentIdForDelete = ref<string | null>(null);

const handleShowDocument = (id: string) => {
  // Check if document is completed - if so, show the final document view
  const completedStatuses = ['Completed', 'Signed', 'Ready for Review or Signature'];
  const isCompleted = completedStatuses.includes(document.status);

  if (isCompleted) {
    // For completed documents, use old flow to show final document
    router.push({
      name: 'new-document-creator',
      params: {
        uuid: id
      }
    });
  } else if (document.template_id) {
    // For incomplete documents with template_id, use new flow
    router.push({
      name: 'document-questions',
      query: {
        template: document.template_id,
        documentId: id
      }
    });
  } else {
    // Fallback to old flow if no template_id
    router.push({
      name: 'new-document-creator',
      params: {
        uuid: id
      }
    });
  }
};

const handleDeleteDocument = (id: string) => {
  documentIdForDelete.value = id;
};

const moveToDirectory = async (id = null) => {
  try {
    await axios.put(`/api/documents/${document.id}/directory`, {
      directory_id: id,
    });
    useToast().success(t("notifications.document_moved"));
    emits("refreshPage");
  } catch (error) {
    useToast().error(t("errors.documents.move_error"));
  }
};

const onDocumentDelete = () => {
  useGTM.pushEvent({
    event: "document_deleted",
    document_template: document.template_id,
  });

  emits("refreshPage");
};

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};
</script>

<template>
  <div class="modern-document-card" @click="handleShowDocument(document.id)">
    <!-- Delete button -->
    <v-btn
      v-if="showDeleteButton"
      icon
      size="small"
      variant="text"
      class="delete-button"
      @click.stop="handleDeleteDocument(document.id)"
    >
      <v-icon size="18">mdi-close</v-icon>
      <v-tooltip activator="parent" location="bottom">{{ t("common.delete") }}</v-tooltip>
    </v-btn>

    <!-- Document header with icon -->
    <div class="document-header">
      <div class="document-icon-container">
        <div class="document-icon">
          <v-icon size="24" color="primary">mdi-file-document-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Document content -->
    <div class="document-content">
      <h3 class="document-title">{{ translateDocumentName(document.name, locale, t) }}</h3>

      <div class="document-meta">
        <div class="meta-item">
          <v-icon size="14" class="meta-icon">mdi-folder-outline</v-icon>
          <span>{{ document.type || t('documents.document') }}</span>
        </div>
        <div class="meta-item">
          <v-icon size="14" class="meta-icon">mdi-calendar-outline</v-icon>
          <span>{{ formatDate(document.created_at) }}</span>
        </div>
        <div v-if="document.directory" class="meta-item">
          <v-icon size="14" class="meta-icon">mdi-folder</v-icon>
          <span>{{ document.directory.name }}</span>
        </div>
      </div>
    </div>

    <!-- Document actions -->
    <div class="document-actions" @click.stop>
      <v-menu v-if="directories?.length" location="top">
        <template v-slot:activator="{ props }">
          <v-btn
            icon
            size="small"
            variant="text"
            class="action-button"
            v-bind="props"
          >
            <v-icon size="18">mdi-folder-move-outline</v-icon>
            <v-tooltip activator="parent" location="top">{{ t("documents.move_to_directory") }}</v-tooltip>
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item
            v-for="directory in filteredDirectoryList"
            :key="directory.id"
            @click="moveToDirectory(directory.id)"
            class="menu-item"
          >
            <template v-slot:prepend>
              <v-icon size="16">mdi-folder</v-icon>
            </template>
            <v-list-item-title class="menu-text">{{ directory.name }}</v-list-item-title>
          </v-list-item>
          <v-divider v-if="filteredDirectoryList.length > 0" class="my-1"></v-divider>
          <v-list-item @click="emits('createDirectory', document.id)" class="menu-item">
            <template v-slot:prepend>
              <v-icon size="16">mdi-folder-plus</v-icon>
            </template>
            <v-list-item-title class="menu-text">{{ t("documents.move_to_new_directory") }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="document.directory"
            @click="moveToDirectory(null)"
            class="menu-item"
          >
            <template v-slot:prepend>
              <v-icon size="16">mdi-folder-remove</v-icon>
            </template>
            <v-list-item-title class="menu-text">{{ t("documents.remove_from_directory") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-btn
        v-if="document.status === 'Ready for Review or Signature' && showSignButton"
        icon
        size="small"
        variant="text"
        class="action-button"
        @click="uploadDocumentToDocusign(document.id)"
      >
        <v-icon size="18">mdi-pen</v-icon>
        <v-tooltip activator="parent" location="top">{{ t("common.sign") }}</v-tooltip>
      </v-btn>

      <v-btn
        icon
        size="small"
        variant="text"
        class="action-button primary-action"
        @click="handleShowDocument(document.id)"
      >
        <v-icon size="18">mdi-eye-outline</v-icon>
        <v-tooltip activator="parent" location="top">{{ t("common.open") }}</v-tooltip>
      </v-btn>
    </div>
  </div>
  <DeleteDocumentDialog
    :isDialogOpen="documentIdForDelete"
    :documentId="documentIdForDelete"
    :documentName="document.name"
    @closeDialog="documentIdForDelete = null"
    @refresh-documents="onDocumentDelete"
  />
</template>

<style scoped lang="scss">
.modern-document-card {
  position: relative;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid #F1F5F9;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: column;
  animation: slideInUp 0.6s ease-out both;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border-color: #E2E8F0;

    .delete-button {
      opacity: 1;
    }

    .action-button {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 12px;
  }
}

.delete-button {
  position: absolute;
  top: 12px;
  right: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 2;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(4px);

  &:hover {
    background: rgba(248, 113, 113, 0.1) !important;
    color: #EF4444 !important;
  }

  @media (max-width: 768px) {
    opacity: 1;
  }
}

.document-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
}

.document-icon-container {
  .document-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #0E25B1 0%, #0A1D8F 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      color: white !important;
    }
  }
}


.document-content {
  flex: 1;
  margin-bottom: 16px;
}

.document-title {
  font-family: 'Archivo', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #1E293B;
  margin: 0 0 12px 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.document-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748B;

  .meta-icon {
    color: #94A3B8 !important;
    flex-shrink: 0;
  }

  span {
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.document-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding-top: 12px;
  border-top: 1px solid #F1F5F9;
}

.action-button {
  opacity: 0;
  transition: all 0.2s ease;

  &.primary-action {
    background: #0E25B1 !important;
    color: white !important;
    opacity: 1;

    &:hover {
      background: #0A1D8F !important;
      transform: scale(1.05);
    }
  }

  &:hover:not(.primary-action) {
    background: rgba(14, 37, 177, 0.08) !important;
    color: #0E25B1 !important;
  }

  @media (max-width: 768px) {
    opacity: 1;
  }
}

// Menu styling
.menu-item {
  .menu-text {
    font-size: 14px;
    font-weight: 500;
  }

  &:hover {
    background: #F8FAFC !important;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .modern-document-card {
    &:hover {
      transform: none;
    }
  }

  .document-title {
    font-size: 15px;
  }

  .meta-item {
    font-size: 12px;
  }

  .document-icon-container .document-icon {
    width: 40px;
    height: 40px;

    .v-icon {
      font-size: 20px !important;
    }
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
